import React from 'react';
import './AccordanceCard.scss';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import Accordance from '../../../../../models/Accordance';
import CommentsIcon from '../CommentsIcon/CommentsIcon';
import { AccordanceType } from '../../../../../models/AccordanceType';
import { replaceMomentFormat } from '../../../../../services/util';
import { capitalizeFirstLetter, parseToSemester } from '../../../../../services/stringUtil';
import { AccordanceState } from '../../../../../models/AccordanceState';
import useAccordanceDetailModalsState from '../../../../../hooks/useAccordanceDetailModalsState';
import AccordanceDetailModals from '../AccordanceDetailModals';
import { RolesIdEnum } from '../../../../../models/RolesIdEnum';
import { ReactComponent as WarningIcon } from '../../../../../assets/pmr-yellow-big.svg';

type Props = {
  accordance?: Accordance | any;
  onClick?: any;
  events?: any;
};

const AccordanceCard = (props: Props) => {
  const { accordance, onClick, events } = props;
  const { hasUnreadComments, accordanceComments, personToApprove, accordanceState, accordanceType, rejectionDetail } =
    accordance;
  const { t } = useTranslation();
  const stateProps = useAccordanceDetailModalsState();
  const { showModal } = stateProps;
  const waitingApprovalLabel = getWaitingApprovalLabel(accordance, t);
  const waitingApprovalContent = getWaitingApprovalContent(waitingApprovalLabel);
  const rejectionCommentCount = accordanceState === AccordanceState.REJECT && rejectionDetail ? 1 : 0;
  const visibleCommentsCount = (accordanceComments || []).filter(item => !item.isDeleted).length + rejectionCommentCount;

  const accordanceInfo = setDefaultValues(accordance, t);

  const arraySort = accordanceInfo.linkedProductsAccordance.sort((a: any, b: any) => {
    if (a.nameProduct < b.nameProduct) {
      return -1;
    }
    if (a.nameProduct > b.nameProduct) {
      return 1;
    }
    return 0;
  });

  const dateColor = () => {
    let currentDate = new Date();
    let dateToExpire =
      accordance?.newExpirationDate !== null ? accordance?.newExpirationDate : accordance.expirationDate;
    let diference = moment(new Date(dateToExpire)).diff(currentDate, 'days');
    if (new Date(dateToExpire) < currentDate) {
      return '#FF0000';
    } else if (diference <= 7) {
      return '#000000';
    } else {
      return '#727e8c';
    }
  };

  const getDueDateAlert = () => {
    let currentDate = new Date();
    let dateToExpire =
      accordance?.newExpirationDate !== null ? accordance?.newExpirationDate : accordance.expirationDate;
    let diference = moment(new Date(dateToExpire)).diff(currentDate, 'days');

    if (!dateToExpire) {
      return <></>;
    }

    if (new Date(dateToExpire) < currentDate) {
      return <span style={{ color: '#FF0000' }}>{" - " + capitalizeFirstLetter(t('expired'))}</span>;
    } else if (diference <= 7) {
      return <span>&nbsp; <WarningIcon style={{ transform: "translateY(3px)" }} /></span>;
    } else {
      return <></>;
    }
  };

  // Se agrega funcion para agregar 2 funciones en mismo onClick

  const handleClickPlus = e => {
    showModal();
    onClick(e);
  };

  return (
    <>
      <div
        className={`accordance-card-container ${setAccordanceTypeClass(accordanceType)} ${setAccordanceStateClass(
          accordanceState,
          waitingApprovalLabel,
        )}`}
        onClick={handleClickPlus}
      >
        <div className="division">
          <span>{accordanceInfo.accordanceAgreementsType.name}</span>
        </div>
        <div className="label-data">
          <span className="label-data-label">{t('accordance_description')}</span>
          <span className="label-data-data" title={accordanceInfo.description}>
            {accordanceInfo.description}
          </span>
        </div>

        <div className="horizontal">
          {
            accordanceType !== AccordanceType.Substantial && (
              <div className="vertical responsible">
                <span className="label">{t('responsible')}</span>
                <span className="data-vertical">
                  {accordanceInfo.personInCharge.name} {accordanceInfo.personInCharge.lastName}
                </span>
              </div>
            )
          }

          {accordanceState !== AccordanceState.FULLFILLED && (
            <div className="vertical">
              <span className="label">{t('due_date')}</span>
              <span
                className="data"
                style={{
                  fontWeight: dateColor() !== '#727e8c' ? 500 : 'normal',
                  color:
                    accordance.newExpirationDate !== null || accordanceState === AccordanceState.FULLFILLED
                      ? '#727e8c'
                      : dateColor(),
                }}
              >
                {accordance.newExpirationDate !== null
                  ? accordanceInfo.newExpirationDate.dateFormated
                  : accordanceInfo.expirationDate.dateFormated}
                {getDueDateAlert()}
              </span>
            </div>
          )}

          {(accordanceType === AccordanceType.Substantial && accordanceInfo.substantialFundamentalChanges?.length > 0) && (
            <div className="vertical substantial-changes">
              <span className="label label-substantial-changes">{t('substantial_fundamental_changes')}</span>
              <div className="title-label-product substantial-items">
                {accordanceInfo.substantialFundamentalChanges?.map(change => {
                  const cleanName = change.nameSubstantialFundamentalChanges.replace(/^\d+(?:\.\d+)*\s*/, '');
                  return <span key={change?.id} className="data-substantial-change">- {cleanName}</span>;
                })}
              </div>
            </div>
          )
          }

          {(accordanceInfo.linkedProductsAccordance.length !== 0 && accordanceType !== AccordanceType.Substantial) && (
            <div className="vertical">
              <span className="label">{t('new_linked_product')}</span>
              <div className="title-label-product">
                {arraySort?.map(element => {
                  return <span key={element?.id} className="data-product">{element.nameProduct}</span>;
                })}
              </div>
            </div>
          )}

          {accordanceState !== AccordanceState.FULLFILLED && visibleCommentsCount > 0 && (
            <div className="vertical">
              <span className="label">{t('comments')}</span>
              <CommentsIcon hasUnreadComments={hasUnreadComments} comments={visibleCommentsCount} />
            </div>
          )}
        </div>

        {accordanceState === AccordanceState.PENDING &&
          waitingApprovalLabel &&
          accordanceType !== AccordanceType.InterGroup && (
            <div className="bottom-gray">
              <span className="label">{t('waiting_approval')}</span>
              <span className="data approval-data">
                {waitingApprovalContent.name && <span className="approval-name">{waitingApprovalContent.name}</span>}
                {waitingApprovalContent.role && <span className="approval-role">{waitingApprovalContent.role}</span>}
              </span>
            </div>
          )}
        {accordanceType === AccordanceType.InterGroup && accordanceState === AccordanceState.PENDING && (
          <div className="bottom-gray">
            <span className="label">{t('waiting_approval')}</span>
            <span className="data">{t('operations_management')}</span>
          </div>
        )}
        {accordanceState === AccordanceState.REJECT && rejectionDetail && rejectionDetail.rejectedBy && (
          <div className="bottom-gray">
            <span className="label">{t('rejected_by')}</span>
            <span className="data approval-data">
              <span className="approval-name">{rejectionDetail.rejectedBy.name} {rejectionDetail.rejectedBy.lastName}</span>
              {waitingApprovalContent.role && <span className="approval-role">{waitingApprovalContent.role}</span>}
            </span>
          </div>
        )}
      </div>

      <AccordanceDetailModals accordance={accordance} stateProps={stateProps} />
    </>
  );
};

export default AccordanceCard;

const setAccordanceTypeClass = (accordanceType: AccordanceType) =>
  accordanceType === AccordanceType.Revision
    ? 'revision'
    : accordanceType === AccordanceType.Monitoring
      ? 'monitor'
      : 'inter-group';

const setAccordanceStateClass = (state: AccordanceState, waitingApprovalLabel?: string) =>
  (state === AccordanceState.PENDING && waitingApprovalLabel) || state === AccordanceState.REJECT
    ? 'extra-height'
    : state === AccordanceState.CANCELLED
      ? 'horizontal-display'
      : state === AccordanceState.DRAFT
        ? 'low-opacity'
        : '';

const getWaitingApprovalLabel = (accordance: Accordance | any, t: (key: string) => string) => {
  const roleToApproveId = accordance?.roleToApprove?.id;
  const pendingApprovalVPSManagerOrThree = accordance?.pendingApprovalVPSManagerOrThree;
  const personToApproveName = accordance?.personToApprove
    ? `${accordance.personToApprove.name} ${accordance.personToApprove.lastName}`
    : '';

  switch (roleToApproveId) {
    case RolesIdEnum.DivChief:
      return personToApproveName ? `${personToApproveName} - ${t('division_chief')}` : '';
    case RolesIdEnum.Country_Representative:
      return personToApproveName ? `${personToApproveName} - ${t('country_representative')}` : '';
    case RolesIdEnum.GeneralManagerVPS:
      if (!pendingApprovalVPSManagerOrThree) {
        return `${t('vps_&_vpc_managers')}`;
      }
      return personToApproveName ? `${personToApproveName} - ${t('vps_sector_manager')}` : '';
    case RolesIdEnum.General_Manager:
      if (!pendingApprovalVPSManagerOrThree) {
        return `${t('vps_&_vpc_managers')}`;
      }
      return personToApproveName ? `${personToApproveName} - ${t('vpc_general_manager')}` : '';
    default:
      return personToApproveName;
  }
};

const getWaitingApprovalContent = (waitingApprovalLabel: string) => {
  const separator = ' - ';

  if (!waitingApprovalLabel.includes(separator)) {
    return {
      name: waitingApprovalLabel,
      role: '',
    };
  }

  const [name, role] = waitingApprovalLabel.split(separator);

  return {
    name,
    role: `${separator}${role}`,
  };
};

export const setDefaultValues = (accordance, t) => {
  return (
    accordance && {
      accordanceAgreementsType: accordance.accordanceAgreementsType || {
        id: '-',
        isActive: '-',
        name: '-',
      },
      description: accordance.description || '-',
      criticalIssueDescription: accordance.criticalIssueDescription || '-',
      linkedProductsAccordance: accordance.linkedProductsAccordance || [],
      rootCauseCategory: accordance.rootCauseCategory || {
        id: '-',
        isActive: '-',
        name: '-',
      },
      criticalIssue: accordance.criticalIssue || {
        id: '-',
        categoryName: '-',
        name: '-',
      },
      personInCharge: accordance.personInCharge || {
        name: '-',
        lastName: '',
      },
      revisionDate: accordance.revisionDate
        ? {
          dateFormated: replaceMomentFormat(moment(accordance.revisionDate).format('DD/MMM/YYYY')),
          dateSemester: parseToSemester(accordance.revisionDate, t),
        }
        : {
          dateFormated: '-',
          dateSemester: '-',
        },
      expirationDate: accordance.expirationDate
        ? {
          dateFormated: replaceMomentFormat(moment(accordance.expirationDate).format('DD/MMM/YYYY')),
          dateFormatedDetails: replaceMomentFormat(moment(accordance.expirationDate).format('DD/MMM/YYYY')),
        }
        : {
          dateFormated: '-',
        },
      newExpirationDate: accordance.newExpirationDate
        ? {
          dateFormated: replaceMomentFormat(moment(accordance.newExpirationDate).format('DD/MMM/YYYY')),
          dateFormatedDetails: replaceMomentFormat(moment(accordance.newExpirationDate).format('DD/MMM/YYYY')),
        }
        : {
          dateFormated: '-',
        },
      accordanceDate: accordance.accordanceDate
        ? {
          dateFormated: replaceMomentFormat(moment(accordance.accordanceDate).format('DD/MMM/YYYY')),
          dateFormatedDetails: replaceMomentFormat(moment(accordance.accordanceDate).format('DD/MMM/YYYY')),
        }
        : {
          dateFormated: '-',
        },
      creationDate: accordance.dateCreated
        ? {
          dateFormated: replaceMomentFormat(moment(accordance.dateCreated).format('MMM/YYYY')),
          dateFormatedDetails: replaceMomentFormat(moment(accordance.dateCreated).format('DD/MMM/YYYY')),
        }
        : {
          dateFormated: '-',
        },
      possibleAction: accordance.possibleAction || {
        id: '-',
        name: '-',
      },
      project: `${accordance.project.code} ${accordance.project.name}`,
      accordanceFiles: accordance.accordanceFiles,
      countNewExpirationDate: accordance.countNewExpirationDate,
      revisionEndDate: accordance.revisionEndDate
        ? {
          dateFormated: replaceMomentFormat(moment(accordance.revisionEndDate).format('DD/MMM/YYYY')),
        }
        : {
          dateFormated: '-',
        },
      agreementManagment: accordance.agreementManagement,
      showAccordanceManagement: accordance.showAccordanceManagement,
      substantialFundamentalChanges: accordance.accordancesSubstantialFundamentalChanges || [],
      status: accordance.accordanceState,
      personToApprove: accordance?.personToApprove
        ? `${accordance.personToApprove.name} ${accordance.personToApprove.lastName}`
        : '',
      rejectionDetail: accordance?.rejectionDetail
    }
  );
};
